// src/components/faqs/FAQFeedbackPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchFaqFeedback,
  fetchFaq,
  clearFaqError,
} from '../../store/actions/faqActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';

const FAQFeedbackPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { faqId } = useParams();
  
  const { 
    selectedFaq: faq,
    feedback,
    feedbackMeta,
    loading,
    error 
  } = useSelector((state) => state.faqs);

  const [page, setPage] = useState(1);
  const [ratingFilter, setRatingFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(20);
  const [stats, setStats] = useState({
    total: 0,
    veryPoor: 0,
    poor: 0,
    neutral: 0,
    good: 0,
    excellent: 0,
    helpful: 0,
    notHelpful: 0,
  });

  const ratingLabels = {
    VERY_POOR: 'Très mauvais',
    POOR: 'Mauvais',
    NEUTRAL: 'Neutre',
    GOOD: 'Bon',
    EXCELLENT: 'Excellent',
  };

  const ratingColors = {
    VERY_POOR: { bg: '#fee2e2', text: '#dc2626', border: '#fecaca' },
    POOR: { bg: '#fed7aa', text: '#c2410c', border: '#fdba74' },
    NEUTRAL: { bg: '#fef3c7', text: '#d97706', border: '#fde68a' },
    GOOD: { bg: '#d1fae5', text: '#059669', border: '#a7f3d0' },
    EXCELLENT: { bg: '#dbeafe', text: '#2563eb', border: '#bfdbfe' },
  };

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      marginBottom: '2rem',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    faqInfo: {
      marginTop: '1rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    faqQuestion: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    faqMeta: {
      display: 'flex',
      gap: '2rem',
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    viewFaqButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      textDecoration: 'none',
      transition: 'all 0.2s',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem',
    },
    statCard: {
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      textAlign: 'center',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statSubValue: {
      fontSize: '0.875rem',
      color: '#059669',
      fontWeight: 500,
    },
    ratingStats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '0.5rem',
      marginBottom: '2rem',
    },
    ratingStat: {
      padding: '1rem',
      borderRadius: '0.5rem',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    ratingStatActive: {
      transform: 'scale(1.02)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    ratingStatValue: {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: '0.25rem',
    },
    ratingStatLabel: {
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    filters: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
    },
    filterButton: {
      padding: '0.5rem 1rem',
      background: 'none',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      cursor: 'pointer',
      color: '#374151',
      transition: 'all 0.2s',
    },
    filterButtonActive: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderColor: '#2563eb',
    },
    searchContainer: {
      width: '300px',
    },
    feedbackList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      marginBottom: '2rem',
    },
    feedbackCard: {
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      transition: 'all 0.2s',
    },
    feedbackHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem',
    },
    feedbackRating: {
      display: 'inline-flex',
      padding: '0.25rem 1rem',
      borderRadius: '9999px',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    feedbackDate: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    feedbackComment: {
      fontSize: '1rem',
      color: '#374151',
      lineHeight: '1.6',
      marginBottom: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      fontStyle: 'italic',
    },
    feedbackMeta: {
      display: 'flex',
      gap: '1.5rem',
      fontSize: '0.875rem',
      color: '#6b7280',
      paddingTop: '0.75rem',
      borderTop: '1px solid #e5e7eb',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    helpfulBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    helpfulYes: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    helpfulNo: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    userAvatar: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    exportButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    exportButtonHover: {
      backgroundColor: '#059669',
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '2px dashed #e5e7eb',
    },
    emptyIcon: {
      width: '4rem',
      height: '4rem',
      marginBottom: '1rem',
      color: '#9ca3af',
    },
    emptyTitle: {
      fontSize: '1.125rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    emptyText: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  useEffect(() => {
    if (faqId) {
      dispatch(fetchFaq(faqId));
      fetchFeedback();
    }
  }, [dispatch, faqId]);

  useEffect(() => {
    fetchFeedback();
  }, [page, ratingFilter, searchTerm]);

  useEffect(() => {
    if (feedback) {
      calculateStats();
    }
  }, [feedback]);

  const fetchFeedback = () => {
    const params = {
      page,
      limit,
      rating: ratingFilter || undefined,
      search: searchTerm || undefined,
    };
    dispatch(fetchFaqFeedback(faqId, params));
  };

  const calculateStats = () => {
    if (!feedback) return;

    const newStats = {
      total: feedbackMeta?.total || 0,
      veryPoor: 0,
      poor: 0,
      neutral: 0,
      good: 0,
      excellent: 0,
      helpful: 0,
      notHelpful: 0,
    };

    feedback.forEach(item => {
      switch (item.rating) {
        case 'VERY_POOR': newStats.veryPoor++; break;
        case 'POOR': newStats.poor++; break;
        case 'NEUTRAL': newStats.neutral++; break;
        case 'GOOD': newStats.good++; break;
        case 'EXCELLENT': newStats.excellent++; break;
      }
      if (item.helpful === true) newStats.helpful++;
      if (item.helpful === false) newStats.notHelpful++;
    });

    setStats(newStats);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleRatingFilter = (rating) => {
    setRatingFilter(rating === ratingFilter ? '' : rating);
    setPage(1);
  };

  const handleExport = () => {
    // Convert feedback to CSV
    const headers = ['Date', 'Note', 'Commentaire', 'Utile', 'Utilisateur', 'Email', 'IP', 'User Agent'];
    const rows = feedback.map(item => [
      new Date(item.createdAt).toLocaleString(),
      ratingLabels[item.rating],
      item.comment || '',
      item.helpful === true ? 'Oui' : item.helpful === false ? 'Non' : 'N/A',
      item.user ? `${item.user.firstName} ${item.user.lastName}` : 'Anonyme',
      item.user?.email || item.userEmail || '',
      item.userIp || '',
      item.userAgent || '',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `faq-${faqId}-feedback-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !feedback) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerLeft}>
            <button
              onClick={() => navigate(`/admin/faqs/${faqId}/details`)}
              style={styles.backButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
              }}
            >
              <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 style={styles.title}>Retours des utilisateurs</h1>
          </div>
          <button
            onClick={handleExport}
            style={styles.exportButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.exportButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.exportButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Exporter CSV
          </button>
        </div>

        {faq && (
          <div style={styles.faqInfo}>
            <div style={styles.faqQuestion}>{faq.question}</div>
            <div style={styles.faqMeta}>
              <span>👁️ {faq.views || 0} vues</span>
              <span>👍 {faq.helpfulCount || 0} utiles</span>
              <span>👎 {faq.notHelpfulCount || 0} non utiles</span>
              <Link to={`/admin/faqs/${faqId}/details`} style={styles.viewFaqButton}>
                Voir la FAQ
              </Link>
            </div>
          </div>
        )}
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearFaqError())}
        />
      )}

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.total}</div>
          <div style={styles.statLabel}>Total des retours</div>
          <div style={styles.statSubValue}>
            {faq && ((stats.total / (faq.views || 1)) * 100).toFixed(1)}% de taux de réponse
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.helpful}</div>
          <div style={styles.statLabel}>Utiles</div>
          <div style={styles.statSubValue}>
            {stats.total > 0 ? ((stats.helpful / stats.total) * 100).toFixed(1) : 0}% des retours
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.notHelpful}</div>
          <div style={styles.statLabel}>Non utiles</div>
          <div style={styles.statSubValue}>
            {stats.total > 0 ? ((stats.notHelpful / stats.total) * 100).toFixed(1) : 0}% des retours
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>
            {faq && ((faq.helpfulCount / (faq.helpfulCount + faq.notHelpfulCount || 1)) * 100).toFixed(1)}%
          </div>
          <div style={styles.statLabel}>Taux d'utilité global</div>
        </div>
      </div>

      {/* Rating Distribution */}
      <div style={styles.ratingStats}>
        {['VERY_POOR', 'POOR', 'NEUTRAL', 'GOOD', 'EXCELLENT'].map(rating => {
          const count = stats[rating.toLowerCase()];
          const color = ratingColors[rating];
          const isActive = ratingFilter === rating;
          
          return (
            <div
              key={rating}
              style={{
                ...styles.ratingStat,
                backgroundColor: color.bg,
                border: `2px solid ${isActive ? color.text : color.border}`,
                ...(isActive ? styles.ratingStatActive : {}),
              }}
              onClick={() => handleRatingFilter(rating)}
            >
              <div style={{ ...styles.ratingStatValue, color: color.text }}>
                {count}
              </div>
              <div style={{ ...styles.ratingStatLabel, color: color.text }}>
                {ratingLabels[rating]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <div style={styles.filters}>
          <button
            style={{
              ...styles.filterButton,
              ...(!ratingFilter ? styles.filterButtonActive : {}),
            }}
            onClick={() => setRatingFilter('')}
          >
            Tous
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(ratingFilter === 'VERY_POOR' ? styles.filterButtonActive : {}),
            }}
            onClick={() => handleRatingFilter('VERY_POOR')}
          >
            Très mauvais
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(ratingFilter === 'POOR' ? styles.filterButtonActive : {}),
            }}
            onClick={() => handleRatingFilter('POOR')}
          >
            Mauvais
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(ratingFilter === 'NEUTRAL' ? styles.filterButtonActive : {}),
            }}
            onClick={() => handleRatingFilter('NEUTRAL')}
          >
            Neutre
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(ratingFilter === 'GOOD' ? styles.filterButtonActive : {}),
            }}
            onClick={() => handleRatingFilter('GOOD')}
          >
            Bon
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(ratingFilter === 'EXCELLENT' ? styles.filterButtonActive : {}),
            }}
            onClick={() => handleRatingFilter('EXCELLENT')}
          >
            Excellent
          </button>
        </div>
        <div style={styles.searchContainer}>
          <SearchBar
            onSearch={handleSearch}
            placeholder="Rechercher dans les commentaires..."
          />
        </div>
      </div>

      {/* Feedback List */}
      {feedback && feedback.length > 0 ? (
        <div style={styles.feedbackList}>
          {feedback.map((item) => (
            <div key={item.id} style={styles.feedbackCard}>
              <div style={styles.feedbackHeader}>
                <span
                  style={{
                    ...styles.feedbackRating,
                    backgroundColor: ratingColors[item.rating]?.bg,
                    color: ratingColors[item.rating]?.text,
                    border: `1px solid ${ratingColors[item.rating]?.border}`,
                  }}
                >
                  {ratingLabels[item.rating]}
                </span>
                <span style={styles.feedbackDate}>
                  {formatDate(item.createdAt)}
                </span>
              </div>

              {item.comment && (
                <div style={styles.feedbackComment}>
                  "{item.comment}"
                </div>
              )}

              <div style={styles.feedbackMeta}>
                {item.helpful !== undefined && (
                  <span style={styles.metaItem}>
                    <span
                      style={{
                        ...styles.helpfulBadge,
                        ...(item.helpful ? styles.helpfulYes : styles.helpfulNo),
                      }}
                    >
                      {item.helpful ? '✓ Utile' : '✗ Pas utile'}
                    </span>
                  </span>
                )}

                {item.user ? (
                  <span style={styles.userInfo}>
                    <span style={styles.userAvatar}>
                      {item.user.firstName?.charAt(0)}
                      {item.user.lastName?.charAt(0)}
                    </span>
                    <span>
                      {item.user.firstName} {item.user.lastName}
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
                        ({item.user.email})
                      </span>
                    </span>
                  </span>
                ) : (
                  <span style={styles.metaItem}>
                    <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Visiteur anonyme
                  </span>
                )}

                {item.userIp && (
                  <span style={styles.metaItem}>
                    <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" />
                    </svg>
                    {item.userIp}
                  </span>
                )}
              </div>

              {item.userAgent && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#9ca3af' }}>
                  User Agent: {item.userAgent}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>
          <svg style={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <div style={styles.emptyTitle}>
            {searchTerm || ratingFilter 
              ? 'Aucun retour trouvé'
              : 'Aucun retour pour cette FAQ'}
          </div>
          <div style={styles.emptyText}>
            {searchTerm || ratingFilter 
              ? 'Essayez de modifier vos filtres de recherche'
              : 'Les retours des utilisateurs apparaîtront ici'}
          </div>
        </div>
      )}

      {/* Pagination */}
      {feedbackMeta && feedbackMeta.totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={feedbackMeta.totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default FAQFeedbackPage;